import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Switch,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { copilotApi } from '../../services/copilotApi';
import { UserWritingTemplate } from '../../types/documents';
import TemplateEditorDialog from './TemplateEditorDialog';

interface WritingTemplatesSettingsProps {
  onClose?: () => void;
}

const WritingTemplatesSettings: React.FC<WritingTemplatesSettingsProps> = () => {
  const [templates, setTemplates] = useState<UserWritingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<UserWritingTemplate | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await copilotApi.getWritingTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load templates:', err);
      setError('Failed to load templates. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setEditorOpen(true);
  };

  const handleEditTemplate = (template: UserWritingTemplate) => {
    setEditingTemplate(template);
    setEditorOpen(true);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await copilotApi.deleteWritingTemplate(templateId);
      setTemplates((prev) => prev.filter((t) => t.templateId !== templateId));
    } catch (err) {
      console.error('Failed to delete template:', err);
      setError('Failed to delete template. Please try again.');
    }
  };

  const handleToggleActive = async (template: UserWritingTemplate) => {
    try {
      const updated = await copilotApi.updateWritingTemplate(template.templateId, {
        isActive: !template.isActive,
      });
      setTemplates((prev) =>
        prev.map((t) => (t.templateId === updated.templateId ? updated : t))
      );
    } catch (err) {
      console.error('Failed to toggle template:', err);
      setError('Failed to update template. Please try again.');
    }
  };

  const handleEditorSave = (template: UserWritingTemplate) => {
    if (editingTemplate) {
      // Update existing
      setTemplates((prev) =>
        prev.map((t) => (t.templateId === template.templateId ? template : t))
      );
    } else {
      // Add new
      setTemplates((prev) => [template, ...prev]);
    }
    setEditorOpen(false);
    setEditingTemplate(null);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingTemplate(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">Writing Templates</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTemplate}
          size="small"
        >
          Add Template
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create custom templates for your clinical documents. The AI will automatically
        use matching templates when generating documents.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {templates.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'grey.50',
          }}
        >
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No templates yet. Create your first template to get started.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddTemplate}>
            Create Template
          </Button>
        </Paper>
      ) : (
        <List disablePadding>
          {templates.map((template) => (
            <Paper
              key={template.templateId}
              variant="outlined"
              sx={{ mb: 1, overflow: 'hidden' }}
            >
              <ListItem
                sx={{
                  bgcolor: template.isActive ? 'transparent' : 'grey.50',
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: template.isActive ? 'text.primary' : 'text.disabled',
                      }}
                    >
                      {template.name}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '300px',
                      }}
                    >
                      {template.useCase}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={template.isActive}
                    onChange={() => handleToggleActive(template)}
                    size="small"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleEditTemplate(template)}
                    sx={{ ml: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteTemplate(template.templateId)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </Paper>
          ))}
        </List>
      )}

      <TemplateEditorDialog
        open={editorOpen}
        template={editingTemplate}
        onSave={handleEditorSave}
        onClose={handleEditorClose}
      />
    </Box>
  );
};

export default WritingTemplatesSettings;
